import { alpha, Theme } from '@mui/material';
import { SIDENAV_TOKENS } from './sidenav-tokens';

/** True when the sidenav uses the light (true-light) surface palette. */
export function isSidenavLightSurface(theme: Theme): boolean {
  return theme.palette.sidenav.surface === 'light';
}

/** Returns the sidenav surface color for the current theme. */
export function getSidenavBackgroundColor(theme: Theme): string {
  if (isSidenavLightSurface(theme)) {
    return theme.palette.sidenav.background;
  }
  return theme.palette.mode === 'dark'
    ? theme.palette.sidenav.backgroundAppDark
    : theme.palette.sidenav.background;
}

/** Returns the sidenav text color for labels and interactive chrome. */
export function getSidenavTextColor(theme: Theme): string {
  return theme.palette.sidenav.text;
}

/** Icon / label accent used on the sidenav surface (nav icons, popover titles). */
export function getSidenavIconColor(theme: Theme): string {
  return theme.palette.sidenav.icon;
}

/** Hover/focus tint for sidenav interactive elements. */
export function getSidenavHoverBackground(theme: Theme): string {
  return theme.palette.sidenav.hover;
}

/** Right-edge border for the drawer (1px structure on light surface). */
export function getSidenavBorderColor(theme: Theme): string {
  return theme.palette.sidenav.border;
}

/** Horizontal rule between sidenav sections. */
export function getSidenavSeparatorColor(theme: Theme): string {
  return theme.palette.sidenav.separator;
}

/** Active leaf label color (stronger than inactive sidenav text). */
export function getSidenavActiveLeafTextColor(theme: Theme): string {
  return theme.palette.sidenav.activeText;
}

/** Brand accent for active ribbons, dots, and icons. */
export function getSidenavAccentColor(theme: Theme): string {
  return theme.palette.sidenav.accent;
}

/** Shared icon button styles for sidenav footer actions. */
export function getSidebarIconButtonSx(theme: Theme) {
  const textColor = getSidenavTextColor(theme);
  const hoverBackground = getSidenavHoverBackground(theme);

  return {
    color: textColor,
    '&:hover': {
      background: hoverBackground,
      color: textColor,
    },
    '&:focus': {
      background: hoverBackground,
      color: textColor,
    },
  };
}

type ToggleButtonSxOptions = {
  size?: number;
};

/**
 * Collapse/expand control in the sidenav footer.
 * Circular resting surface so the chevron reads as a dedicated control.
 */
export function getSidenavToggleButtonSx(
  theme: Theme,
  { size = SIDENAV_TOKENS.toggleButtonSize }: ToggleButtonSxOptions = {},
) {
  const textColor = getSidenavTextColor(theme);
  const hoverBackground = getSidenavHoverBackground(theme);

  return {
    ...getSidebarIconButtonSx(theme),
    // Larger than size="small" so the control stays centered in the collapsed rail.
    width: size,
    height: size,
    backgroundColor: alpha(textColor, 0.08),
    color: alpha(textColor, 0.75),
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      backgroundColor: hoverBackground,
      color: textColor,
    },
    '&:focus': {
      backgroundColor: hoverBackground,
      color: textColor,
    },
  };
}

type NotificationsButtonSxOptions = {
  /** When false, mute the bell so it does not compete with brand/account. */
  hasNotifications?: boolean;
};

/** Notification bell in the identity row. */
export function getSidenavNotificationsButtonSx(
  theme: Theme,
  { hasNotifications = false }: NotificationsButtonSxOptions = {},
) {
  const textColor = getSidenavTextColor(theme);

  return {
    ...getSidebarIconButtonSx(theme),
    // Nudge into row padding so the bell center aligns with the account chevron.
    mr: -0.5,
    color: hasNotifications ? textColor : alpha(textColor, 0.55),
  };
}
